import * as API from "../../../api/file_manager.js";

const messageTranslation = {
  unknown_response: "Unknown error response from connector",
  "TypeError: Failed to fetch": "Cannot get a response from connector.",
};

/**
 * Clean path string removing double slashes and prepending a slash
 * @param {String} path
 * @returns {String}
 */
const fixPath = (path) => {
  return ("/" + path).replace(/\/\//g, "/");
};

/**
 * Wrap API response for retrive file liest
 * @param {String} path
 * @returns {Object}
 */
export const getFileList = (path) => {
  path = fixPath(path);
  return API.list(path);
};

/**
 * Wrap API response for retrive file content
 * @param {String} path
 * @returns {Object}
 */
export const getFileBody = (path, filename) => {
  path = fixPath(path + "/" + filename);
  return API.getFileContent(path);
};

export const getFileDownload = (path, filename) => {
  path = fixPath(path + "/" + filename);
  return API.getFileContentDownload(path);
};

/**
 * Wrap API response for retrive file content
 * @param {String} path
 * @param {String} filename
 * @param {String} newFileName
 * @param {Boolean} overwrite
 * @returns {Object}
 */
export const renameItem = (path, filename, newFileName, overwrite = false) => {
  const oldPath = fixPath(path + "/" + filename);
  const newPath = fixPath(path + "/" + newFileName);
  return API.rename(oldPath, newPath, overwrite);
};

/**
 * Wrap API response for create folder
 * @param {String} path
 * @param {String} folder
 * @returns {Object}
 */
export const createFolder = (path, folder) => {
  path = fixPath(path);
  if (!(folder || "").trim()) {
    return "Invalid folder name";
  }
  return API.createDirectory(path, folder);
};

/**
 * Wrap API response for remove file or folder
 * @param {String} path
 * @param {Array} filenames
 * @param {Boolean} recursive
 * @returns {Object}
 */
export const removeItems = (path, filenames, recursive = true) => {
  path = fixPath(path);
  if (!filenames.length) {
    return "No files to remove";
  }
  return API.remove(path, filenames, recursive);
};

export const zipFileOrFolder = (path, filenames, recursive = true) => {
  path = fixPath(path);
  if (!filenames.length) {
    return "No file or folder to zip";
  }
  return API.zip(path, filenames, recursive);
};

export const UnZipFile = (path, filenames, recursive = true) => {
  path = fixPath(path);
  if (!filenames.length) {
    return "No file no unzip";
  }
  return API.unzip(path, filenames, recursive);
};

/**
 * Wrap API response for move file or folder
 * @param {String} path
 * @param {Array} filenames
 * @param {Boolean} recursive
 * @returns {Object}
 */
export const moveItems = (path, destination, filenames, saveAsMap = {}) => {
  path = fixPath(path);
  destination = fixPath(destination);
  if (!filenames.length) {
    return "No files to move";
  }
  return API.move(path, destination, filenames, saveAsMap);
};

/**
 * Wrap API response for copy file or folder
 * @param {String} path
 * @param {Array} filenames
 * @param {Boolean} recursive
 * @returns {Object}
 */
export const copyItems = (dispatch, path, destination, filenames, saveAsMap = {}) => {
  path = fixPath(path);
  destination = fixPath(destination);
  if (!filenames.length) {
    return "No files to copy";
  }
  return API.copy(dispatch, path, destination, filenames, saveAsMap);
};

/**
 * Wrap API response for upload files
 * @param {String} path
 * @param {Object<FileList>} fileList
 * @returns {Object}
 */
export const uploadFiles = (dispatch, path, fileList) => {
  path = fixPath(path);
  if (!fileList.length) {
    return "No files to upload";
  }
  fileList.forEach((file) => {
    API.uploadFiles(dispatch, file, path, file.name);
  });
};

/**
 * Calculate available actions for a file
 * @param {Object} file
 * @returns {Array<String>}
 */
export const getActionsByFile = (file, acts = []) => {
  if (file.type === "dir") {
    acts.push("open");

    typeof file.compressible !== "undefined"
      ? file.compressible && acts.push("compress")
      : acts.push("compress");
    acts.push("zip");
  }

  if (file.type === "file") {
    const isArchiveFile = /\.(zip|rar|tar\.gz|tar|7z)$/i.test(file.name);
    if (file.unzipable == true || isArchiveFile) {
      acts.push("unzip");
    }
    acts.push("download");
  }

  acts.push("move");
  acts.push("copy");
  acts.push("rename");
  acts.push("remove");

  return acts;
};

/**
 * Calculate available actions for selected files, excluding non coincidences
 * @param {Array<Object>} files
 * @returns {Array<String>}
 */
export const getActionsByMultipleFiles = (files, acts = []) => {
  files.forEach((file) => {
    const fileActs = getActionsByFile(file);
    // intersects previous actions with the following to leave only coincidences
    acts = acts.length
      ? acts.filter((value) => -1 !== fileActs.indexOf(value))
      : fileActs;
  });

  if (files.length > 1) {
    acts.splice(acts.indexOf("open"), acts.indexOf("open") >= 0);
    acts.splice(acts.indexOf("edit"), acts.indexOf("edit") >= 0);
    acts.splice(acts.indexOf("zip"), acts.indexOf("zip") >= 0);
    acts.splice(acts.indexOf("unzip"), acts.indexOf("unzip") >= 0);
    acts.splice(acts.indexOf("compress"), acts.indexOf("compress") >= 0);
    acts.splice(acts.indexOf("download"), acts.indexOf("download") >= 0);
    acts.splice(acts.indexOf("rename"), acts.indexOf("rename") >= 0);
    acts.push("compress");
  }
  return acts;
};

/**
 * Calculate file size by bytes in human readable format
 * @param {Number} bytes
 * @returns {String}
 */
export const getHumanReadableFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const e = (Math.log(bytes) / Math.log(1e3)) | 0;
  return (
    +(bytes / Math.pow(1e3, e)).toFixed(2) +
    " " +
    ("kMGTPEZY"[e - 1] || "") +
    "B"
  );
};
